import * as React from "react";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  MenuItem,
  MenuList,
  Popover,
  Snackbar,
  SnackbarOrigin,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { useDispatch, useSelector } from "react-redux";

import { fetchArticle } from "../../../../services/tecdocService";
import theme from "../../../../theme";
import {
  addProductToBasket,
  getLiveArticleState,
} from "../../../../services/core-api";
import { addToCart } from "../../../../store/reducers/basket";
import { Article } from "../../../../interfaces/Article";
import { BasketItem } from "../../../../interfaces/core/BasketItem";
import { useSnackbar } from "../../../common/SnackbarContext";
import {
  getUserPrice,
  getUserRetailPrice,
  normalizeAutoPartnerPrice,
  formatPriceWithCurrency,
  getCurrentUserCurrency,
} from "../../../../utils/pricingUtils";
import { useQuery } from "@tanstack/react-query";

const defaultImage = "./no-image.png";

/* =========================================================
   STYLES
   ========================================================= */

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.text.secondary,
    color: theme.palette.text.primary,
    padding: "5px !important",
    fontSize: "13px",
    fontFamily: "Inter",
    fontWeight: 500,
  },
  [`&.${tableCellClasses.body}`]: {
    padding: "5px !important",
    fontSize: "12px",
    fontFamily: "Inter",
    fontWeight: 400,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

interface RootState {
  priceReducer: {
    retailPrice: boolean;
  };
}

interface State extends SnackbarOrigin {
  openNotification: boolean;
}

/* =========================================================
   COMPONENT
   ========================================================= */

const ProductItemPartList = ({ articleNumber, dataSupplierId }: any) => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();

  const retailPriceEnabled = useSelector(
    (state: RootState) => state.priceReducer.retailPrice
  );

  const [availability, setAvailability] = React.useState<any | null>(null);
  const [price, setPrice] = React.useState(0);
  const [priceRetail, setPriceRetail] = React.useState(0);
  const [quantity, setQuantity] = React.useState(0);
  const [value, setValue] = React.useState(1);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);

  const articleNumberModified = (articleNumber ?? "").replace(/\//g, "*");
  const canFetchLive = Boolean(articleNumberModified && dataSupplierId != null && dataSupplierId !== "");

  const { data: article, isLoading: articleLoading } = useQuery({
    queryKey: ["article", articleNumber],
    queryFn: () => fetchArticle(articleNumber),
    enabled: Boolean(articleNumber),
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });

  const { data: liveArticleData, isLoading: liveLoading } = useQuery({
    queryKey: ["liveArticle", articleNumberModified, dataSupplierId],
    queryFn: ({ signal }) =>
      getLiveArticleState(
        { articleNumber: articleNumberModified, dataSupplierId, quantity: 1 },
        signal
      ),
    enabled: canFetchLive,
    staleTime: Infinity,
    gcTime: 30 * 60 * 1000,
  });

  React.useEffect(() => {
    if (!canFetchLive) {
      setAvailability(null);
      setQuantity(0);
      setPrice(0);
      setPriceRetail(0);
      setValue(0);
      return;
    }
    if (liveArticleData) {
      setAvailability(liveArticleData);
      const total = Math.max(0, liveArticleData.totalStock ?? 0);
      setQuantity(total);
      setValue(total > 0 ? 1 : 0);
      const normalizedPrice = normalizeAutoPartnerPrice(
        liveArticleData.priceNet ?? 0,
        liveArticleData.priceRetail ?? 0
      );
      setPrice(getUserPrice(normalizedPrice));
      setPriceRetail(getUserRetailPrice(normalizedPrice));
    }
  }, [canFetchLive, liveArticleData]);

  /* =========================================================
     ADD TO BASKET
     ========================================================= */
  const addToBasket = () => {
    if (!article) return;

    const item: BasketItem = {
      productId: article.productId ?? 0,
      articleNumber: article.articleNumber,
      internalNumber: article.internalNumber ?? "",
      mfrName: article.mfrName,
      dataSupplierId: article.dataSupplierId,
      image: article.images?.[0]?.imageURL200 ?? defaultImage,
      description:
        article.genericArticles?.[0]?.genericArticleDescription ?? "",
      price,
      quantity: value,
      totalPrice: +(price * value).toFixed(2),
      available: true,
      currency: getCurrentUserCurrency(),
    };

    const userStr = localStorage.getItem("user");
    if (!userStr) {
      showSnackbar("Please log in to add products to basket.", "error");
      return;
    }

    const parsedUser = JSON.parse(userStr);
    const user_id = parsedUser.id;
    if (!user_id) {
      showSnackbar("Please log in to add products to basket.", "error");
      return;
    }

    (async () => {
      try {
        await addProductToBasket(user_id, item);
        dispatch(addToCart(item));
        showSnackbar("Product added to basket successfully!", "success");
      } catch (error) {
        showSnackbar("Failed to add product to basket.", "error");
      }
    })();
  };

  /* =========================================================
     RENDER
     ========================================================= */
  const showArticleSkeletons = articleLoading || !article;
  const showPriceStockSkeletons = canFetchLive && (liveLoading || availability === null);

  return (
    <StyledTableRow>
      <StyledTableCell>
        {showArticleSkeletons ? (
          <Skeleton variant="text" animation="wave" sx={{ width: 80, height: 20 }} />
        ) : (
          article!.articleNumber
        )}
      </StyledTableCell>

      <StyledTableCell>
        {showArticleSkeletons ? (
          <Skeleton variant="rectangular" animation="wave" sx={{ width: 40, height: 26 }} />
        ) : (
          <img
            src={article!.images?.[0]?.imageURL1600 ?? defaultImage}
            style={{ height: 26 }}
            alt=""
          />
        )}
      </StyledTableCell>

      <StyledTableCell>
        {showArticleSkeletons ? (
          <Skeleton variant="text" animation="wave" sx={{ width: 120, height: 20 }} />
        ) : (
          <>
            {article!.mfrName},{" "}
            {article!.genericArticles?.[0]?.genericArticleDescription}
          </>
        )}
      </StyledTableCell>

      <StyledTableCell align="right">
        {showPriceStockSkeletons ? (
          <Skeleton
            variant="text"
            animation="wave"
            sx={{ width: "60px", height: "24px", display: "inline-block" }}
          />
        ) : (
          formatPriceWithCurrency(
            retailPriceEnabled ? priceRetail : price
          )
        )}
      </StyledTableCell>

      <StyledTableCell align="right">
        <Box display="flex" justifyContent="flex-end" alignItems="center">
          {showPriceStockSkeletons ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{ width: 32, height: 32, borderRadius: 0 }}
              />
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{ width: 50, height: 36 }}
              />
              <Skeleton
                variant="rectangular"
                animation="wave"
                sx={{ width: 36, height: 36, borderRadius: 0 }}
              />
            </Stack>
          ) : (
            <>
              <Button
                startIcon={
                  <img
                    src="./available_status.svg"
                    width={16}
                    height={16}
                    alt=""
                  />
                }
                sx={{
                  background:
                    quantity < 1
                      ? "#F33"
                      : quantity === 1
                      ? "#FFA500"
                      : "#0C0",
                  minWidth: 32,
                }}
                onMouseEnter={(e) => setAnchorEl(e.currentTarget)}
                onMouseLeave={() => setAnchorEl(null)}
              />
              <TextField
                value={value}
                onChange={(e) => setValue(Math.max(1, +e.target.value))}
                size="small"
                sx={{ width: 50, mx: 1 }}
                disabled={quantity < 1}
              />
              <Button
                disabled={quantity < 1 || value > quantity}
                sx={{
                  background: theme.palette.secondary.main,
                  minWidth: 36,
                }}
                onClick={addToBasket}
              >
                <ShoppingCartOutlinedIcon sx={{ color: "#fff", fontSize: 16 }} />
              </Button>
            </>
          )}
        </Box>

        {availability?.warehouses && (
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
          >
            <MenuList>
              {availability.warehouses.map((w: any, i: number) => (
                <MenuItem key={i}>
                  {w.DepartamentCode} — {w.InStock}
                </MenuItem>
              ))}
            </MenuList>
          </Popover>
        )}
      </StyledTableCell>
    </StyledTableRow>
  );
};

export default ProductItemPartList;
